import {t} from '@lingui/macro';
import _ from 'lodash';
import {createSelector} from 'reselect';

import {hasRoleInNamespace, isTeamAdmin} from '../../helpers/models/accessHelpers';
import {CompanyFeaturesEnum, isCompanyFeaturesEnum} from '../../models/api/companyModel';
import {isPermissionsEnum, PermissionsEnum} from '../../models/api/roleModel';
import {
  isSettingsItemWithHideConditions,
  isSettingsItemWithShowConditions,
  SettingsMenuBlockTypesEnum,
  SettingsMenuDefinition,
  SettingsMenuHomeBlock,
  SettingsMenuItem,
  SettingsMenuItemRequirementTypesEnum,
  SettingsMenuItemsBlock,
  SettingsMenuSectionTypesEnum,
} from '../../settings/menu/settingsMenuCommonTypes';
import {getHasPermission} from '../data/access/accessSelectors';
import {getAdminDnsDomains} from '../data/channels/channelsSelectors';
import {
  getCompanyHasHiddenBilling,
  getFindHasCompanyFeature,
  getIsInTrialOrManagedTrial,
} from '../data/company/companySelectors';
import {getAccess} from '../data/dataSelectors';
import {getIsUserGlobalAdmin, getUserNamespace} from '../data/teammates/teammatesSelectors';
import {
  getFirstTeamUserIsAdminOf,
  getFirstTeamUserIsPartOf,
  getFirstTeamWithLimitedSettingsAccess,
  getFlatTeams,
} from '../data/teams/teamsSelectors';
import {
  getHasDeveloperSettings,
  getHasNewIntegrationsDiscoverability,
  getHasUpsellCards,
  getInitialFeatureFlags,
} from '../featureFlags/featureFlagsSelectors';
import {FeatureFlagsEnum, isFeatureFlagsEnum} from '../featureFlags/featureFlagsTypes';
import {getLocalize} from '../stateless/internationalizationSelectors';

export const getFilterCompanySettingsMenuItem = createSelector(
  [
    getFlatTeams,
    getHasPermission,
    getIsUserGlobalAdmin,
    getAccess,
    getCompanyHasHiddenBilling,
    getAdminDnsDomains,
    getInitialFeatureFlags,
    getFindHasCompanyFeature,
    getFirstTeamUserIsPartOf,
    getHasNewIntegrationsDiscoverability,
  ],
  (
      teams,
      hasPermission,
      isGlobalAdmin,
      access,
      hasHiddenBilling,
      adminDnsDomains,
      featureFlags,
      findHasCompanyFeature,
      firstTeamUserIsPartOf,
      hasNewIntegrationsDiscoverability,
    ) =>
    (item: SettingsMenuItem): boolean => {
      const firstTeam = firstTeamUserIsPartOf;

      // We need to hide the old integrations tab when they have the new app store.
      // TODO: Remove this when hasNewIntegrationsDiscoverability has been fully rolled out.
      if (item.pathId === 'settings.integrations' && hasNewIntegrationsDiscoverability) {
        return false;
      }

      if (!isSettingsItemWithShowConditions(item) && !isSettingsItemWithHideConditions(item)) {
        return true;
      }

      if (isSettingsItemWithShowConditions(item)) {
        return item.showConditions.every((requirement) => {
          if (requirement === SettingsMenuItemRequirementTypesEnum.ADMIN && !isGlobalAdmin) {
            return false;
          }

          if (requirement === SettingsMenuItemRequirementTypesEnum.NOT_ADMIN && isGlobalAdmin) {
            return false;
          }

          if (requirement === SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM && teams.length > 1) {
            return false;
          }

          if (requirement === SettingsMenuItemRequirementTypesEnum.MULTIPLE_TEAMS && teams.length < 2) {
            return false;
          }

          if (
            requirement === SettingsMenuItemRequirementTypesEnum.TEAM_ADMIN &&
            !isTeamAdmin(firstTeam?.namespace ?? '', access)
          ) {
            return false;
          }

          if (
            isPermissionsEnum(requirement) &&
            !hasPermission(firstTeam?.namespace ?? '', requirement, true)
          ) {
            return false;
          }

          if (requirement === SettingsMenuItemRequirementTypesEnum.BILLING && hasHiddenBilling) {
            return false;
          }

          if (isFeatureFlagsEnum(requirement) && !featureFlags[requirement]) {
            return false;
          }

          if (isCompanyFeaturesEnum(requirement) && !findHasCompanyFeature(requirement)) {
            return false;
          }

          if (
            requirement === SettingsMenuItemRequirementTypesEnum.DELIVERABILITY &&
            adminDnsDomains.length === 0
          ) {
            return false;
          }

          return true;
        });
      }

      if (isSettingsItemWithHideConditions(item)) {
        return item.hideConditions.every((hideCondition) => {
          if (featureFlags[hideCondition]) {
            return false;
          }

          return true;
        });
      }

      return true;
    },
);

export const getFilterTeamSettingsItem = createSelector(
  [getHasPermission, getAccess, getIsUserGlobalAdmin, getInitialFeatureFlags, getFindHasCompanyFeature],
  (hasPermission, access, isGlobalAdmin, featureFlags, findHasCompanyFeature) =>
    (item: SettingsMenuItem, namespace: string): boolean => {
      if (!isSettingsItemWithShowConditions(item) && !isSettingsItemWithHideConditions(item)) {
        return true;
      }

      if (isSettingsItemWithShowConditions(item)) {
        return item.showConditions.every((requirement) => {
          if (requirement === SettingsMenuItemRequirementTypesEnum.ADMIN && !isGlobalAdmin) {
            return false;
          }

          if (isPermissionsEnum(requirement) && !hasPermission(namespace, requirement, true)) {
            return false;
          }

          if (
            requirement === SettingsMenuItemRequirementTypesEnum.TEAM_ADMIN &&
            !isTeamAdmin(namespace, access)
          ) {
            return false;
          }

          if (isCompanyFeaturesEnum(requirement) && !findHasCompanyFeature(requirement)) {
            return false;
          }

          if (isFeatureFlagsEnum(requirement) && !featureFlags[requirement]) {
            return false;
          }

          return true;
        });
      }

      if (isSettingsItemWithHideConditions(item)) {
        return item.hideConditions.every((hideCondition) => {
          if (featureFlags[hideCondition]) {
            return false;
          }

          return true;
        });
      }

      return true;
    },
);

export const getFilterPersonalSettingsItem = createSelector(
  [getFindHasCompanyFeature, getInitialFeatureFlags],
  (findHasCompanyFeature, featureFlags) =>
    (item: SettingsMenuItem): boolean => {
      if (!isSettingsItemWithShowConditions(item) && !isSettingsItemWithHideConditions(item)) {
        return true;
      }

      if (isSettingsItemWithShowConditions(item)) {
        return item.showConditions.every((requirement) => {
          if (isFeatureFlagsEnum(requirement) && !featureFlags[requirement]) {
            return false;
          }

          if (isCompanyFeaturesEnum(requirement) && !findHasCompanyFeature(requirement)) {
            return false;
          }

          return true;
        });
      }

      if (isSettingsItemWithHideConditions(item)) {
        return item.hideConditions.every((hideCondition) => {
          if (featureFlags[hideCondition]) {
            return false;
          }

          return true;
        });
      }

      return true;
    },
);

export const getSettingsMenuBlocks = createSelector(
  [
    getHasUpsellCards,
    getIsInTrialOrManagedTrial,
    getLocalize,
    getFindHasCompanyFeature,
    getFilterCompanySettingsMenuItem,
    getUserNamespace,
    getFlatTeams,
    getFilterPersonalSettingsItem,
    getFilterTeamSettingsItem,
    getIsUserGlobalAdmin,
    getAccess,
    getHasDeveloperSettings,
  ],
  (
    hasUpsellCards,
    isInTrialOrManagedTrial,
    localize,
    findHasCompanyFeature,
    filterCompanySettingsMenuItem,
    userNamespace,
    teams,
    filterPersonalSettingsItem,
    filterTeamSettingsItem,
    isGlobalAdmin,
    access,
    hasDeveloperSettings,
  ): SettingsMenuDefinition => {
    const shouldShowUpsells = hasUpsellCards && !isInTrialOrManagedTrial;
    const hasCustomViews = findHasCompanyFeature(CompanyFeaturesEnum.CUSTOM_VIEWS);

    const firstTeamUserIsPartOf = teams.find((team) => hasRoleInNamespace(access, team.namespace));
    const firstTeamUserIsAdminOf = teams.find((team) => isTeamAdmin(team.namespace, access));
    const hasSingleTeam = teams.length === 1 && firstTeamUserIsPartOf;

    const companyItemTemplates: ReadonlyArray<SettingsMenuItem> = [
      {
        name: localize(t('settingsmenu-teams')`Teams`),
        pathId: 'settings.teams',
        showConditions: [SettingsMenuItemRequirementTypesEnum.ADMIN],
        hasUpsell: shouldShowUpsells && !findHasCompanyFeature(CompanyFeaturesEnum.TEAMS),
      },
      {
        name: localize(t('settingsmenu-teammates')`Teammates`),
        pathId: 'settings.teammates',
        parameters: {
          namespace: 'global',
        },
        showConditions: [SettingsMenuItemRequirementTypesEnum.ADMIN],
      },
      {
        name: localize(t('settingsmenu-teammates')`Teammates`),
        pathId: 'settings.teammates',
        showConditions: [
          SettingsMenuItemRequirementTypesEnum.NOT_ADMIN,
          SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM,
          PermissionsEnum.TEAMMATE_UPDATE,
        ],
      },
      {
        name: localize(t('settingsmenu-circles')`Teammate Groups`),
        pathId: 'settings.circles',
        parameters: {
          namespace: 'global',
        },
        showConditions: [FeatureFlagsEnum.CIRCLES, SettingsMenuItemRequirementTypesEnum.ADMIN],
      },
      {
        name: localize(t('settingsmenu-inboxes')`Inboxes`),
        pathId: 'settings.inboxes',
        showConditions: [SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM, PermissionsEnum.INBOX_UPDATE],
      },
      {
        name: localize(t('settingsmenu-rules')`Rules`),
        pathId: 'settings.rules',
        showConditions: [SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM, PermissionsEnum.RULE_UPDATE],
        hasUpsell:
          shouldShowUpsells &&
          (!findHasCompanyFeature(CompanyFeaturesEnum.GLOBAL_RULES) ||
            !findHasCompanyFeature(CompanyFeaturesEnum.SLA_RULES) ||
            !findHasCompanyFeature(CompanyFeaturesEnum.ADVANCED_RULES)),
      },
      {
        name: localize(t('settingsmenu-rules')`Rules`),
        pathId: 'settings.rules',
        parameters: {
          namespace: 'global',
        },
        showConditions: [
          SettingsMenuItemRequirementTypesEnum.MULTIPLE_TEAMS,
          PermissionsEnum.RULE_UPDATE,
          CompanyFeaturesEnum.GLOBAL_RULES,
          SettingsMenuItemRequirementTypesEnum.ADMIN,
        ],
      },
      {
        name: localize(t('settingsmenu-custom-fields')`Custom Fields`),
        pathId: 'settings.custom-fields',
        parameters: {
          namespace: 'global',
          tabpage: 'cards',
        },
        omittedParametersForActiveStatus: ['tabpage'],
        showConditions: [SettingsMenuItemRequirementTypesEnum.ADMIN],
      },
      {
        name: localize(t('settingsmenu-messagetemplates')`Message templates`),
        pathId: 'settings.answers',
        showConditions: [
          SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM,
          PermissionsEnum.CANNED_ANSWER_UPDATE,
        ],
      },
      {
        name: localize(t('settingsmenu-signatures')`Signatures`),
        pathId: 'settings.signatures',
        showConditions: [SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM, PermissionsEnum.SIGNATURE_UPDATE],
      },
      {
        name: localize(t('settingsmenu-views')`Views`),
        pathId: 'settings.custom-views',
        showConditions: [SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM, PermissionsEnum.CUSTOM_VIEW_EDIT],
        hasUpsell: shouldShowUpsells && !hasCustomViews,
      },
      {
        name: localize(t('settingsmenu-tags')`Tags`),
        pathId: 'settings.tags',
        showConditions: [SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM, PermissionsEnum.TAG_UPDATE],
      },
      {
        name: localize(t('settingsmenu-roles')`Roles & Permissions`),
        pathId: 'settings.roles',
        showConditions: [SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM, PermissionsEnum.ROLE_UPDATE],
        hasUpsell: shouldShowUpsells && !findHasCompanyFeature(CompanyFeaturesEnum.ROLES),
      },
      {
        name: localize(t('settingsmenu-integrations')`Integrations`),
        pathId: 'settings.integrations',
        showConditions: [SettingsMenuItemRequirementTypesEnum.ADMIN],
      },
      {
        name: localize(t('settingsmenu-tools')`Plugins & API`),
        pathId: 'settings.tools',
        showConditions: [SettingsMenuItemRequirementTypesEnum.ADMIN],
        hasUpsell: shouldShowUpsells && !findHasCompanyFeature(CompanyFeaturesEnum.API),
      },
      {
        name: localize(t('settingsmenu-app-store')`App store`),
        pathId: 'settings.apps',
        showConditions: [FeatureFlagsEnum.NEW_INTEGRATIONS_DISCOVERABILITY],
      },
      {
        name: localize(t('settingsmenu-new-applications')`Applications`),
        pathId: 'settings.applications',
        showConditions: [SettingsMenuItemRequirementTypesEnum.ADMIN, FeatureFlagsEnum.LOCAL_APPLICATIONS],
      },
      {
        name: localize(t('settingsmenu-preferences')`Preferences`),
        pathId: 'settings.companyPreferences.general',
        showConditions: [SettingsMenuItemRequirementTypesEnum.ADMIN],
      },
      {
        name: localize(t('settingsmenu-billing')`Billing`),
        pathId: 'settings.billing',
        showConditions: [
          SettingsMenuItemRequirementTypesEnum.ADMIN,
          SettingsMenuItemRequirementTypesEnum.BILLING,
        ],
      },
      {
        name: localize(t('settingsmenu-deliverability')`Deliverability`),
        pathId: 'settings.deliverability',
        showConditions: [
          SettingsMenuItemRequirementTypesEnum.ADMIN,
          SettingsMenuItemRequirementTypesEnum.DELIVERABILITY,
        ],
      },
      {
        name: localize(t('settingsmenu-shifts')`Shifts`),
        pathId: 'settings.shifts',
        showConditions: [
          SettingsMenuItemRequirementTypesEnum.ADMIN,
          SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM,
          CompanyFeaturesEnum.SHIFTS,
        ],
      },
    ];

    const companyItems = companyItemTemplates
      .filter((item) => filterCompanySettingsMenuItem(item))
      .map((item) => {
        if (!item.showConditions?.includes(SettingsMenuItemRequirementTypesEnum.SINGLE_TEAM)) {
          return item;
        }

        return {
          ...item,
          parameters: {
            ...item.parameters,
            namespace: firstTeamUserIsPartOf?.namespace,
          },
        };
      });

    const companyBlock: SettingsMenuItemsBlock = {
      type: SettingsMenuBlockTypesEnum.SECTION,
      title: hasSingleTeam ? firstTeamUserIsPartOf?.name || '' : localize(t('settingsmenu-company')`Company`),
      items: companyItems,
      team: hasSingleTeam ? firstTeamUserIsPartOf : undefined,
      sectionId: `company-${firstTeamUserIsPartOf?.namespace}`,
      sectionType: SettingsMenuSectionTypesEnum.COMPANY,
    };

    const teamItemTemplates: ReadonlyArray<SettingsMenuItem> = [
      {
        name: localize(t('settingsmenu-teammates')`Teammates`),
        pathId: 'settings.teammates',
        showConditions: [PermissionsEnum.TEAMMATE_UPDATE],
      },
      {
        name: localize(t('settingsmenu-inboxes')`Inboxes`),
        pathId: 'settings.inboxes',
        showConditions: [PermissionsEnum.INBOX_UPDATE],
      },
      {
        name: localize(t('settingsmenu-rules')`Rules`),
        pathId: 'settings.rules',
        showConditions: [PermissionsEnum.RULE_UPDATE],
      },
      {
        name: localize(t('settingsmenu-messagetemplates')`Message templates`),
        pathId: 'settings.answers',
        showConditions: [PermissionsEnum.CANNED_ANSWER_UPDATE],
      },
      {
        name: localize(t('settingsmenu-signatures')`Signatures`),
        pathId: 'settings.signatures',
        showConditions: [PermissionsEnum.SIGNATURE_UPDATE],
      },
      {
        name: localize(t('settingsmenu-views')`Views`),
        pathId: 'settings.custom-views',
        showConditions: [PermissionsEnum.CUSTOM_VIEW_EDIT],
        hasUpsell: shouldShowUpsells && !hasCustomViews,
      },
      {
        name: localize(t('settingsmenu-tags')`Tags`),
        pathId: 'settings.tags',
        showConditions: [PermissionsEnum.TAG_UPDATE],
      },
      {
        name: localize(t('settingsmenu-roles')`Roles & Permissions`),
        pathId: 'settings.roles',
        showConditions: [PermissionsEnum.ROLE_UPDATE],
      },
      {
        name: localize(t('settingsmenu-shifts')`Shifts`),
        pathId: 'settings.shifts',
        showConditions: [PermissionsEnum.EDIT_SHIFT, CompanyFeaturesEnum.SHIFTS],
      },
    ];

    const teamBlocks = _(teams)
      .filter((team) => Boolean(isGlobalAdmin || hasRoleInNamespace(access, team.namespace)))
      .sortBy((team) => team.name)
      .map(
        (team) =>
          ({
            type: SettingsMenuBlockTypesEnum.SECTION,
            title: team.name,
            team,
            sectionId: team.namespace,
            sectionType: SettingsMenuSectionTypesEnum.TEAM,
            items: teamItemTemplates
              .filter((item) => filterTeamSettingsItem(item, team.namespace))
              .map((i) => ({
                ...i,
                parameters: {
                  ...i.parameters,
                  namespace: team.namespace,
                },
              })),
          } as SettingsMenuItemsBlock),
      )
      .value();

    const personalItemTemplates: ReadonlyArray<SettingsMenuItem> = [
      {
        name: localize(t('settingsmenu-profile')`Profile`),
        pathId: 'settings.profile',
      },
      {
        name: localize(t('settingsmenu-userpreferences')`Preferences`),
        pathId: 'settings.myPreferences',
        parameters: {
          namespace: userNamespace,
        },
      },
      {
        name: localize(t('settingsmenu-inboxes')`Inboxes`),
        pathId: 'settings.inboxes',
        parameters: {
          namespace: userNamespace,
        },
      },
      {
        name: localize(t('settingsmenu-rules')`Rules`),
        pathId: 'settings.rules',
        parameters: {
          namespace: userNamespace,
        },
      },
      {
        name: localize(t('settingsmenu-messagetemplates')`Message templates`),
        pathId: 'settings.answers',
        parameters: {
          namespace: userNamespace,
        },
      },
      {
        name: localize(t('settingsmenu-responders')`Vacation responders`),
        pathId: 'settings.responder.list',
        parameters: {
          namespace: userNamespace,
        },
      },
      {
        name: localize(t('settingsmenu-signatures')`Signatures`),
        pathId: 'settings.signatures',
        parameters: {
          namespace: userNamespace,
        },
      },
      {
        name: localize(t('settingsmenu-tags')`Tags`),
        pathId: 'settings.tags',
        parameters: {
          namespace: userNamespace,
        },
      },
      {
        name: localize(t('settingsmenu-notifications')`Notifications`),
        pathId: 'settings.notifications',
      },
      {
        name: localize(t('settingsmenu-snoozes')`Snoozes`),
        pathId: 'settings.snoozes.list',
        parameters: {
          namespace: userNamespace,
        },
      },
      {
        name: localize(t('settingsmenu-shifts')`Shifts`),
        pathId: 'settings.userShifts',
        showConditions: [CompanyFeaturesEnum.SHIFTS],
      },
    ];

    const personalBlock: SettingsMenuItemsBlock = {
      type: SettingsMenuBlockTypesEnum.SECTION,
      title: localize(t('settingsmenu-personal')`Personal`),
      items: personalItemTemplates.filter((i) => filterPersonalSettingsItem(i)),
      sectionId: userNamespace,
      sectionType: SettingsMenuSectionTypesEnum.PERSONAL,
    };

    const personalHomeBlock: SettingsMenuHomeBlock = {
      type: SettingsMenuBlockTypesEnum.HOME,
      title: localize(t('settingsmenu-personalhome')`My home`),
      pathId: 'settings.user-home',
    };

    const adminHomeBlock: SettingsMenuHomeBlock = {
      type: SettingsMenuBlockTypesEnum.HOME,
      title: localize(t('settingsmenu-adminhome')`Admin home`),
      pathId: 'settings.home',
    };

    const developersBlock: SettingsMenuHomeBlock = {
      type: SettingsMenuBlockTypesEnum.HOME,
      title: localize(t('settingsmenu-developers')`Developers`),
      pathId: 'settings.developers',
      icon: 'developers',
    };

    const shouldShowAdminHome = isGlobalAdmin || Boolean(firstTeamUserIsAdminOf);
    const shouldShowDevelopersHome = isGlobalAdmin && hasDeveloperSettings;

    const baseResult = {
      personal: [personalHomeBlock, personalBlock],
    };

    return {
      ...baseResult,
      company: _([
        shouldShowAdminHome && adminHomeBlock,
        shouldShowDevelopersHome && developersBlock,
        companyBlock,
      ])
        .compact()
        .concat(hasSingleTeam ? [] : teamBlocks)
        .filter(filterSettingsBlock)
        .value(),
    };
  },
);

function filterSettingsBlock(block: SettingsMenuHomeBlock | SettingsMenuItemsBlock) {
  if (block.type === SettingsMenuBlockTypesEnum.HOME) {
    return true;
  }

  return block.items.length > 0;
}

export const getSettingsCompanyHomePath = createSelector(
  [getIsUserGlobalAdmin, getFirstTeamWithLimitedSettingsAccess, getFirstTeamUserIsAdminOf],
  (isUserGlobalAdmin, firstTeamWithLimitedSettingsAccess, firstTeamUserIsAdminOf) => {
    if (firstTeamUserIsAdminOf || isUserGlobalAdmin) {
      return '/settings/home';
    }

    if (firstTeamWithLimitedSettingsAccess) {
      const {limitedSettings, namespace} = firstTeamWithLimitedSettingsAccess;

      return `/settings/${namespace}/${limitedSettings?.path}`;
    }

    return undefined;
  },
);
